/**
 * Schemas de validation Zod pour B8ild
 *
 * Ces schemas sont utilises pour valider les donnees cote client
 * avant envoi a Supabase ou aux Edge Functions.
 */

import { z } from "zod";

// =============================================================================
// UTILITAIRES
// =============================================================================

// Regex pour format date YYYY-MM-DD
const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

// Regex pour UUID
const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Validation UUID
export const uuidSchema = z.string().regex(uuidRegex, "UUID invalide");

// Validation date format YYYY-MM-DD
export const dateSchema = z.string().regex(dateRegex, "Format date invalide (YYYY-MM-DD)");

// =============================================================================
// CHANTIER (Projet)
// =============================================================================

export const chantierSchema = z.object({
  nom_chantier: z.string()
    .min(1, "Le nom du chantier est requis")
    .max(100, "100 caracteres maximum")
    .trim(),
  reference_chantier: z.string().max(50, "50 caracteres maximum").optional().nullable(),
  client: z.string()
    .min(1, "Le client est requis")
    .max(100, "100 caracteres maximum")
    .trim(),
  adresse: z.string().max(200, "200 caracteres maximum").optional().nullable(),
  budget_ht: z.number()
    .nonnegative("Le budget doit etre positif")
    .max(100000000, "Budget maximum depasse"),
  duree_estimee_jours: z.number()
    .positive("Doit etre positif")
    .max(3650, "Maximum 10 ans")
    .optional()
    .nullable(),
  date_debut_prevue: dateSchema.optional().nullable(),
  date_fin_estimee: dateSchema.optional().nullable(),
  date_fin_reelle: dateSchema.optional().nullable(),
  description: z.string().max(2000, "2000 caracteres maximum").optional().nullable(),
  etat_chantier: z.enum([
    "prospect",
    "devis",
    "actif",
    "en_cours",
    "planifie",
    "brouillon",
    "en_pause",
    "suspendu",
    "projection",
    "termine",
    "archive"
  ]).default("prospect"),
  // Stocke le statut avant archivage pour restauration lors du désarchivage
  etat_avant_archivage: z.string().nullable().optional(),
  pictogramme: z.string().max(50).optional().nullable(),
  couleur: z.string().max(20).optional().nullable(),
});

// Schema pour la creation (champs obligatoires)
export const createChantierSchema = chantierSchema.pick({
  nom_chantier: true,
  client: true,
  budget_ht: true,
}).extend({
  etat_chantier: chantierSchema.shape.etat_chantier.optional(),
});

// Schema pour l'edition (tous les champs optionnels sauf l'id)
export const updateChantierSchema = chantierSchema.partial();

export type ChantierFormData = z.infer<typeof chantierSchema>;
export type CreateChantierData = z.infer<typeof createChantierSchema>;
export type UpdateChantierData = z.infer<typeof updateChantierSchema>;

// =============================================================================
// AFFECTATION (Equipe sur chantier)
// =============================================================================

export const affectationSchema = z.object({
  chantier_id: uuidSchema,
  membre_equipe_id: uuidSchema.optional().nullable(),
  equipe_id: uuidSchema.optional().nullable(),
  date_debut: dateSchema,
  date_fin: dateSchema,
  jours_travailles: z.number()
    .int("Doit etre un nombre entier")
    .positive("Doit etre positif")
    .max(365, "Maximum 365 jours"),
  heures_par_jour: z.number()
    .positive("Doit etre positif")
    .max(24, "Maximum 24 heures"),
  taux_horaire_specifique: z.number()
    .positive("Doit etre positif")
    .max(1000, "Maximum 1000 euros/heure")
    .optional()
    .nullable(),
  notes: z.string().max(500, "500 caracteres maximum").optional().nullable(),
}).refine(
  data => data.membre_equipe_id || data.equipe_id,
  { message: "Un membre ou une equipe doit etre selectionne" }
).refine(
  data => new Date(data.date_fin) >= new Date(data.date_debut),
  { message: "La date de fin doit etre apres la date de debut", path: ["date_fin"] }
);

export type AffectationFormData = z.infer<typeof affectationSchema>;

// =============================================================================
// OCR - Donnees extraites
// =============================================================================

export const ocrFactureSchema = z.object({
  fournisseur: z.string()
    .min(1, "Le fournisseur est requis")
    .max(100, "100 caracteres maximum")
    .trim(),
  montant_ht: z.number().nonnegative("Le montant HT doit etre positif"),
  tva_pct: z.number()
    .min(0, "La TVA ne peut pas etre negative")
    .max(100, "TVA maximum 100%")
    .default(20),
  montant_ttc: z.number().nonnegative("Le montant TTC doit etre positif"),
  tva_montant: z.number().nonnegative().optional(),
  date_facture: dateSchema,
  siret: z.string()
    .regex(/^\d{14}$/, "Le SIRET doit contenir 14 chiffres")
    .optional()
    .nullable(),
  numero_facture: z.string().max(50, "50 caracteres maximum").optional().nullable(),
  categorie: z.string().max(50).optional().nullable(),
});

export const ocrDevisSchema = z.object({
  montant_ht: z.number().nonnegative("Le montant HT doit etre positif"),
  tva: z.number()
    .min(0, "La TVA ne peut pas etre negative")
    .max(100, "TVA maximum 100%")
    .default(20),
  montant_ttc: z.number().nonnegative("Le montant TTC doit etre positif"),
  statut: z.enum(["brouillon", "envoye", "accepte", "refuse"]).default("brouillon"),
});

export const ocrFraisSchema = z.object({
  type_frais: z.string()
    .min(1, "Le type de frais est requis")
    .max(100, "100 caracteres maximum"),
  montant_ht: z.number().nonnegative("Le montant doit etre positif"),
  description: z.string().max(500, "500 caracteres maximum").optional().nullable(),
  date_frais: dateSchema,
});

export type OcrFactureData = z.infer<typeof ocrFactureSchema>;
export type OcrDevisData = z.infer<typeof ocrDevisSchema>;
export type OcrFraisData = z.infer<typeof ocrFraisSchema>;

// =============================================================================
// FACTURE FOURNISSEUR (Edition)
// =============================================================================

export const factureEditSchema = z.object({
  fournisseur: z.string()
    .min(1, "Le fournisseur est requis")
    .max(100, "100 caracteres maximum")
    .trim(),
  montant_ht: z.number().nonnegative("Le montant HT doit etre positif"),
  tva_pct: z.number()
    .min(0, "La TVA ne peut pas etre negative")
    .max(100, "TVA maximum 100%"),
  montant_ttc: z.number().nonnegative("Le montant TTC doit etre positif"),
  siret: z.string()
    .regex(/^\d{14}$/, "Le SIRET doit contenir 14 chiffres")
    .optional()
    .nullable()
    .or(z.literal("")),
  categorie: z.string().min(1, "La categorie est requise"),
  date_facture: dateSchema,
});

export type FactureEditData = z.infer<typeof factureEditSchema>;

// =============================================================================
// ENTREPRISE (Métier & Localisation)
// =============================================================================

// Liste des métiers valides (doit correspondre à benchmarkMetiers.ts)
export const metierKeySchema = z.string().max(50).default('autre');

export const localisationTypeSchema = z.enum(['province', 'metropole']).default('province');

export const entrepriseMetierSchema = z.object({
  metier_key: metierKeySchema,
  localisation_type: localisationTypeSchema,
});

export type EntrepriseMetierData = z.infer<typeof entrepriseMetierSchema>;
export type LocalisationType = z.infer<typeof localisationTypeSchema>;

// =============================================================================
// MEMBRE EQUIPE
// =============================================================================

export const membreEquipeSchema = z.object({
  prenom: z.string()
    .min(1, "Le prenom est requis")
    .max(50, "50 caracteres maximum")
    .trim(),
  nom: z.string()
    .min(1, "Le nom est requis")
    .max(50, "50 caracteres maximum")
    .trim(),
  poste: z.string().max(100, "100 caracteres maximum").optional().nullable(),
  taux_horaire: z.number()
    .nonnegative("Le taux horaire doit etre positif")
    .max(1000, "Maximum 1000 euros/heure"),
  email: z.string().email("Email invalide").optional().nullable().or(z.literal("")),
  telephone: z.string().max(20, "20 caracteres maximum").optional().nullable(),
});

export type MembreEquipeData = z.infer<typeof membreEquipeSchema>;

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Valide les donnees et retourne le resultat ou les erreurs formatees
 */
export function validateData<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: string[] } {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  // Zod v4 utilise .issues au lieu de .errors
  const errors = result.error.issues.map(e => {
    const path = e.path.length > 0 ? `${e.path.join(".")}: ` : "";
    return `${path}${e.message}`;
  });

  return { success: false, errors };
}

/**
 * Valide un UUID
 */
export function isValidUUID(value: string): boolean {
  return uuidRegex.test(value);
}

/**
 * Valide un format date YYYY-MM-DD
 */
export function isValidDate(value: string): boolean {
  return dateRegex.test(value);
}
